// src/pages/EditMenuPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  // REMOVED: getRestaurantBySlug (using ID now)
  getMenuItems,
  addMenuItem,
  deleteMenuItem 
} from '../services/firebaseService';
import { trackAdminAction } from '../services/analyticsService'; // Import analytics function
import { uploadFile, validateFile } from '../utils/uploadHelper';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, addDoc, deleteDoc } from 'firebase/firestore'; // Keep doc, getDoc, updateDoc
import { db, storage } from '../firebase'; // Import storage
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'; // Import storage functions
import { getCurrencySymbol } from '../config/currencies'; // Removed SUPPORTED_CURRENCIES
import { QRCodeCanvas } from 'qrcode.react'; // Import QR Code component
import Switch from '../components/Switch'; // Import the Switch component
import { FaUserCircle, FaClipboardList, FaPlus, FaTrash, FaUtensils } from 'react-icons/fa'; // Import an account icon and orders icon
import useOrderNotifications from '../hooks/useOrderNotifications'; // Import the custom hook
import LoadingSpinner from '../components/loadingspinner';// Import the loading spinner
import './EditMenuPage.css'; // Import the CSS file for styling

function EditMenuPage() {
  const { restaurantId } = useParams(); // Changed from slug to restaurantId
  const location = useLocation(); // Get location for URL parameters
  const navigate = useNavigate();
  
  // Get menuId from URL parameters if present
  const urlParams = new URLSearchParams(location.search);
  const menuId = urlParams.get('menuId');
  
  useOrderNotifications(restaurantId); // Use the hook for notifications
  // REMOVED: const { slug } = useParams(); // No longer needed if using ID
  const [restaurant, setRestaurant] = useState(null);
  const [currentMenu, setCurrentMenu] = useState(null); // Add current menu state
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(''); // For success feedback
  const [searchQuery, setSearchQuery] = useState(''); // For menu item search
  const [selectedPortions, setSelectedPortions] = useState({}); // Track selected portion for each item

  // New menu item form
  const [showForm, setShowForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    offerPrice: '',
    prepTime: '',
    category: '',
    photoUrl: '',
    isHidden: false, // Add isHidden state
    isPortionWise: false, // Add portion-wise toggle
    portions: [] // Changed from individual portion fields to array
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showQrCode, setShowQrCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // Portion dialog states
  const [showPortionDialog, setShowPortionDialog] = useState(false);
  const [portionFormData, setPortionFormData] = useState({
    name: '',
    price: '',
    description: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        console.log("EditMenuPage: Fetching data for restaurant ID:", restaurantId);
        console.log("EditMenuPage: Menu ID from URL:", menuId);

        // Get restaurant info using ID
        const restaurantDocRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantDocRef);

        if (!restaurantSnap.exists()) {
          console.error("EditMenuPage: Restaurant not found for ID:", restaurantId);
          setError('Restaurant not found. Please check the URL and try again.');
          setLoading(false);
          return;
        }
        const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
        console.log("EditMenuPage: Restaurant data received:", restaurantData);
        
        setRestaurant(restaurantData);
        console.log("EditMenuPage: Restaurant state updated");
        
        // If menuId is provided, fetch menu-specific items
        if (menuId) {
          console.log("EditMenuPage: Fetching menu-specific items for menu ID:", menuId);
          
          // Get the specific menu info
          const menuDocRef = doc(db, 'restaurants', restaurantId, 'menus', menuId);
          const menuSnap = await getDoc(menuDocRef);
          
          if (menuSnap.exists()) {
            const menuData = { id: menuSnap.id, ...menuSnap.data() };
            setCurrentMenu(menuData);
            console.log("EditMenuPage: Menu data received:", menuData);
            
            // Get menu items for this specific menu
            const menuItemsRef = collection(db, 'restaurants', restaurantId, 'menus', menuId, 'menuItems');
            const menuItemsSnap = await getDocs(menuItemsRef);
            const items = menuItemsSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            console.log("EditMenuPage: Menu-specific items received:", items.length);
          setMenuItems(items);
          
          // Initialize selected portions for portion-wise items (default to quarter)
          const initialPortions = {};
          items.forEach(item => {
            if (item.isPortionWise && item.portions && item.portions.length > 0) {
              initialPortions[item.id] = item.portions[0].name; // Default to first portion
            }
          });
          setSelectedPortions(initialPortions);
          } else {
            setError('Menu not found. Please check the URL and try again.');
            setLoading(false);
            return;
          }
        } else {
          // Get default menu items (backward compatibility)
          console.log("EditMenuPage: Fetching default menu items for restaurant ID:", restaurantData.id);
          const items = await getMenuItems(restaurantData.id);
          console.log("EditMenuPage: Default menu items received:", items.length);
          setMenuItems(items);
          
          // Initialize selected portions for portion-wise items (default to quarter)
          const initialPortions = {};
          items.forEach(item => {
            if (item.isPortionWise && item.portions && item.portions.length > 0) {
              initialPortions[item.id] = item.portions[0].name; // Default to first portion
            }
          });
          setSelectedPortions(initialPortions);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('EditMenuPage: Error fetching data:', error);
        let errorMessage = 'Failed to load data. ';
        if (error.code === 'permission-denied') {
          errorMessage += 'You do not have permission to access this data.';
        } else if (error.code === 'not-found') {
          errorMessage += 'The restaurant or its menu was not found.';
        } else {
          errorMessage += 'Please try again later or contact support.';
        }
        setError(errorMessage);
        setLoading(false);
      }
    };

    if (restaurantId) { // Fetch only if restaurantId is present
      fetchData();
    }
  }, [restaurantId, menuId]); // Depend on restaurantId and menuId

  // --- Helper Functions ---
  const handlePortionChange = (itemId, portion) => {
    setSelectedPortions(prev => ({
      ...prev,
      [itemId]: portion
    }));
  };

  const getDisplayPrice = (item) => {
    if (!item.isPortionWise || !item.portions || item.portions.length === 0) {
      return item.price;
    }
    
    const selectedPortion = selectedPortions[item.id] || (item.portions[0] ? item.portions[0].name : '');
    const portion = item.portions.find(p => p.name === selectedPortion);
    return portion ? portion.price : item.price;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      offerPrice: '',
      prepTime: '',
      category: '',
      photoUrl: '',
      isHidden: false,
      isPortionWise: false,
      portions: [] // Reset portions array
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditingItemId(null);
    setShowForm(false);
    setUploadProgress(0);
    setError(''); // Also clear any previous errors
    setSuccessMessage(''); // Clear success messages too
    
    // Reset portion dialog state
    setShowPortionDialog(false);
    setPortionFormData({
      name: '',
      price: '',
      description: ''
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = validateFile(file, {
        maxSizeMB: 5,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });

      if (validation !== true) {
        setError(validation);
        setPhotoFile(null);
        setPhotoPreview(formData.photoUrl || null); // Reset preview if validation fails
      } else {
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
        setError(''); // Clear error if validation passes
      }
    } else {
      // If no file is selected (e.g., user cancels), reset
      setPhotoFile(null);
      // Keep existing photoUrl if editing, otherwise null
      setPhotoPreview(formData.photoUrl || null);
    }
  };

  // --- Menu Item Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle numeric inputs
    if (name === 'price' || name === 'offerPrice' || name === 'prepTime') {
      // Only allow numbers and decimal point
      const isValid = /^\d*\.?\d*$/.test(value);
      if (!isValid && value !== '') {
        return;
      }
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEditItem = (item) => {
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price ? item.price.toString() : '',
      offerPrice: item.offerPrice ? item.offerPrice.toString() : '',
      prepTime: item.prepTime ? item.prepTime.toString() : '',
      category: item.category || '',
      photoUrl: item.photoUrl || '',
      isHidden: item.isHidden || false, // Load isHidden status, default to false
      isPortionWise: item.isPortionWise || false, // Load portion-wise status
      portions: item.portions || [] // Load portions array
    });
    setPhotoPreview(item.photoUrl || null);
    setEditingItemId(item.id);
    setShowForm(true);
    
    // Scroll to the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generateQrCode = () => {
    if (restaurant && restaurant.slug) {
      const url = `${window.location.origin}/${restaurant.slug}`;
      setQrCodeUrl(url);
      setShowQrCode(true);
    } else {
      setError('Cannot generate QR code: Restaurant data not loaded or slug missing.');
    }
  };

  // Portion dialog handlers
  const handleAddPortion = () => {
    setPortionFormData({
      name: '',
      price: '',
      description: ''
    });
    setShowPortionDialog(true);
  };

  const handlePortionInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle numeric input for price
    if (name === 'price') {
      const isValid = /^\d*\.?\d*$/.test(value);
      if (!isValid && value !== '') {
        return;
      }
    }
    
    setPortionFormData({
      ...portionFormData,
      [name]: value
    });
  };

  const handleSavePortion = () => {
    if (!portionFormData.name || !portionFormData.price) {
      setError('Portion name and price are required');
      return;
    }

    // Check if portion name already exists
    if (formData.portions.some(p => p.name.toLowerCase() === portionFormData.name.toLowerCase())) {
      setError('Portion name already exists');
      return;
    }

    const newPortion = {
      id: Date.now().toString(), // Simple ID generation
      name: portionFormData.name,
      price: parseFloat(portionFormData.price),
      description: portionFormData.description
    };

    setFormData({
      ...formData,
      portions: [...formData.portions, newPortion]
    });

    setShowPortionDialog(false);
    setPortionFormData({
      name: '',
      price: '',
      description: ''
    });
    setError('');
  };

  const handleCancelPortion = () => {
    setShowPortionDialog(false);
    setPortionFormData({
      name: '',
      price: '',
      description: ''
    });
    setError('');
  };

  const handleDeletePortion = (portionId) => {
    setFormData({
      ...formData,
      portions: formData.portions.filter(p => p.id !== portionId)
    });
  };

  const downloadQrCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');
      let downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      // Use restaurant slug for the filename if available
      const filename = restaurant && restaurant.slug ? `${restaurant.slug}-menu-qr-code.png` : 'menu-qr-code.png';
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.name || !formData.category) {
      setError('Name and category are required');
      return;
    }
    
    // Validate pricing based on portion-wise setting
    if (formData.isPortionWise) {
      if (!formData.portions || formData.portions.length < 2) {
        setError('At least two portions are required when portion-wise is enabled');
        return;
      }
    } else {
      if (!formData.price) {
        setError('Price is required');
        return;
      }
    }
    
    // Validate photo file if provided
    if (photoFile) {
      const validation = validateFile(photoFile, {
        maxSizeMB: 5,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });
      
      if (validation !== true) {
        setError(validation);
        return;
      }
    }
    
    try {
      setIsSubmitting(true);
      setError('');
      
      console.log("Submitting menu item form:", editingItemId ? "Update" : "Add");
      console.log("Form data:", formData);
      console.log("Photo file:", photoFile ? {
        name: photoFile.name,
        type: photoFile.type,
        size: photoFile.size
      } : "No photo file");
      
      const processedData = {
        ...formData,
        price: formData.isPortionWise ? null : (formData.price ? parseFloat(formData.price) : null),
        offerPrice: formData.offerPrice ? parseFloat(formData.offerPrice) : null,
        prepTime: formData.prepTime ? parseInt(formData.prepTime) : null,
        isHidden: formData.isHidden, // Include isHidden in processed data
        isPortionWise: formData.isPortionWise, // Include portion-wise setting
        portions: formData.isPortionWise ? formData.portions : [] // Include portions array
      };
      
      let photoUrl = formData.photoUrl;
      
      // Upload photo if provided
      if (photoFile) {
        try {
          console.log("Uploading photo...");
          setUploadProgress(0);
          
          photoUrl = await uploadFile(
            photoFile, 
            `restaurants/${restaurant.id}/menu`, 
            (progress) => {
              console.log(`Photo upload progress: ${progress}%`);
              setUploadProgress(progress);
            }
          );
          
          console.log("Photo uploaded successfully:", photoUrl);
          setUploadProgress(100);
        } catch (uploadError) {
          console.error("Error uploading photo:", uploadError);
          setError(`Failed to upload photo: ${uploadError.message}`);
          setIsSubmitting(false);
          setUploadProgress(0);
          return;
        }
      }
      
      // Add the photoUrl to processed data
      processedData.photoUrl = photoUrl;
      
      let result;
      
      if (editingItemId) {
        // Update existing item
        console.log("Updating menu item:", editingItemId);
        
        try {
          // Update the menu item document - use menu-specific path if menuId exists
          const menuItemPath = menuId 
            ? `restaurants/${restaurant.id}/menus/${menuId}/menuItems`
            : `restaurants/${restaurant.id}/menuItems`;
          const menuItemRef = doc(db, menuItemPath, editingItemId);
          
          await updateDoc(menuItemRef, {
            ...processedData,
            updatedAt: new Date()
          });
          
          result = {
            id: editingItemId,
            ...processedData
          };
          
          console.log("Update successful:", result);
        } catch (updateError) {
          console.error("Error updating menu item:", updateError);
          setError(`Failed to update menu item: ${updateError.message}`);
          setIsSubmitting(false);
          return;
        }
        
        // Update local state
        setMenuItems(menuItems.map(item => 
          item.id === editingItemId 
            ? { ...result } 
            : item
        ));
      } else {
        // Add new item
        console.log("Adding new menu item");
        
        try {
          if (menuId) {
            // Add to specific menu
            const menuItemsRef = collection(db, 'restaurants', restaurant.id, 'menus', menuId, 'menuItems');
            const docRef = await addDoc(menuItemsRef, {
              ...processedData,
              createdAt: new Date(),
              updatedAt: new Date()
            });
            
            result = {
              id: docRef.id,
              ...processedData
            };
          } else {
            // Add to default menu (backward compatibility)
            result = await addMenuItem(restaurant.id, processedData, photoFile);
          }
          
          console.log("Add successful:", result);
        } catch (addError) {
          console.error("Error adding menu item:", addError);
          setError(`Failed to add menu item: ${addError.message}`);
          setIsSubmitting(false);
          return;
        }
        
        // Update local state
        setMenuItems([...menuItems, result]);
      }
      
      // Reset form
      resetForm();
      setShowForm(false);
      setIsSubmitting(false);
      
      // Show success message using state
      setSuccessMessage(editingItemId ? "Menu item updated successfully!" : "Menu item added successfully!");
      trackAdminAction(editingItemId ? 'update_menu_item' : 'add_menu_item', { restaurantId: restaurant.id, itemId: result.id, itemName: result.name });
      // Clear message after a few seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(`Failed to save menu item: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const handleToggleVisibility = async (itemId, currentVisibility) => {
    const newVisibility = !currentVisibility;
    try {
      // Use menu-specific path if menuId exists
      const menuItemPath = menuId 
        ? `restaurants/${restaurant.id}/menus/${menuId}/menuItems`
        : `restaurants/${restaurant.id}/menuItems`;
      const menuItemRef = doc(db, menuItemPath, itemId);
      
      await updateDoc(menuItemRef, {
        isHidden: newVisibility,
        updatedAt: new Date()
      });

      // Update local state
      setMenuItems(menuItems.map(item => 
        item.id === itemId 
          ? { ...item, isHidden: newVisibility } 
          : item
      ));
      trackAdminAction('toggle_menu_item_visibility', { restaurantId: restaurant.id, itemId, newVisibility });
    } catch (error) {
      console.error('Error toggling visibility:', error);
      setError(`Failed to update item visibility: ${error.message}`);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      if (menuId) {
        // Delete from specific menu
        const menuItemRef = doc(db, 'restaurants', restaurant.id, 'menus', menuId, 'menuItems', itemId);
        await deleteDoc(menuItemRef);
      } else {
        // Delete from default menu (backward compatibility)
        await deleteMenuItem(restaurant.id, itemId);
      }
      
      // Update local state
      setMenuItems(menuItems.filter(item => item.id !== itemId));
      
      // If we're editing this item, reset the form
      trackAdminAction('delete_menu_item', { restaurantId: restaurant.id, itemId });
      if (editingItemId === itemId) {
        resetForm();
        setShowForm(false);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete menu item');
    }
  };

  // Filter menu items based on search query
  const filteredMenuItems = searchQuery.trim() === '' 
    ? menuItems 
    : menuItems.filter(item => {
        const query = searchQuery.toLowerCase();
        return (
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.description && item.description.toLowerCase().includes(query)) ||
          (item.category && item.category.toLowerCase().includes(query))
        );
      });

  // Group filtered menu items by category
  const groupedItems = filteredMenuItems.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !restaurant) {
    return (
      <div className="container centered">
        <p className="error-message">{error}</p>
        <Link to="/" className="btn mt-4">Go Home</Link>
      </div>
    );
  }

  return (
    <div className="container">
          {/* Edit Restaurant Details Button */}
      {/* Header Section */}
      <div className="header" style={{
        position: 'relative',
        width: '100%'
      }}>
        
        
        <div className="header-content" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          paddingTop: '10px'
        }}>
  
          {/* Restaurant Logo - Full Width */}
            <div style={{
              width: '100%',
              marginBottom: '0px',
              position: 'relative' // Added for positioning context
            }}>
              <button 
                className="btn button-primary" 
                style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  zIndex: 10,
                  textDecoration: 'none',
                  padding: '8px 8px',
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  maxWidth: '60px',
                  overflow: 'hidden'
                }}
                onClick={() => navigate(`/manage-menus/${restaurantId}`)} // Navigate to manage menus page
              >
            Back
              </button>
              {restaurant?.logoUrl && (
              <img 
                src={restaurant.logoUrl} 
                alt={`${restaurant.name} Logo`} 
                style={{ 
                  width: '100%',
                  height: 'auto',
                  maxHeight: '150px',
                  objectFit: 'cover'
                }} 
              />
            )}
            </div>
      <div style={{
          textAlign: 'center',
          width: '100%',
          marginTop: '10px'}}>
          <h1 style={{ 
            textAlign: 'center',
            margin: '0',
            fontSize: '2rem',
            fontWeight: 'bold',
            width: '100%'
          }}>{restaurant?.name}</h1>
          
          {/* Display current menu name if editing a specific menu */}
          {currentMenu && (
            <h2 style={{
              textAlign: 'center',
              margin: '10px 0 0 0',
              fontSize: '1.5rem',
              fontWeight: 'normal',
              color: '#666',
              width: '100%'
            }}>
              Editing: {currentMenu.name}
              {currentMenu.isActive && (
                <span style={{
                  marginLeft: '10px',
                  padding: '4px 8px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                  ACTIVE
                </span>
              )}
            </h2>
          )}
          
        </div>
        
      </div>

      {/* Portion Dialog */}
      {showPortionDialog && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCancelPortion()}>
          <div className="modal-content">
            <h3>Add New Portion</h3>
            <div className="form-group">
              <label htmlFor="portionName">Portion Name *</label>
              <input
                type="text"
                id="portionName"
                name="name"
                className="form-control"
                value={portionFormData.name}
                onChange={handlePortionInputChange}
                placeholder="e.g. Small, Medium, Large"
                autoFocus
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="portionPrice">Price * ({getCurrencySymbol(restaurant?.currency)})</label>
              <input
                type="text"
                id="portionPrice"
                name="price"
                className="form-control"
                value={portionFormData.price}
                onChange={handlePortionInputChange}
                placeholder="e.g. 9.99"
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="portionDescription">Description</label>
              <textarea
                id="portionDescription"
                name="description"
                className="form-control"
                value={portionFormData.description}
                onChange={handlePortionInputChange}
                placeholder="Brief description of this portion (optional)"
                rows="3"
              />
            </div>
            
            <div className="modal-buttons">
              <button
                type="button"
                onClick={handleCancelPortion}
                className="btn cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePortion}
                className="btn save-btn"
                disabled={!portionFormData.name.trim() || !portionFormData.price}
              >
                Add Portion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      
      
      {/* Error Display */}
      {error && (
        <div className="error-message mb-4">
          {error}
        </div>
      )}
      {/* Success Message Display */}
      {successMessage && (
        <div className="success-message mb-4">
          {successMessage}
        </div>
      )}

      <div className="menu-items">
        <h1 style={{ 
          textAlign: 'center',
          fontSize: '1.7rem',
          fontWeight: 'bold',
          paddingTop: '.5rem',
          width: '100%'
        }}>Edit Menu</h1>
      </div>
      
      {/* Main Action Buttons */}
      <div className="edit-controls" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.4rem', // Reduced gap
        width: '100%',
        overflowX: 'auto', // Enable horizontal scrolling
        paddingBottom: '0.5rem', // Add some padding at the bottom for scrollbar visibility
  
      }}>
    
        <button 
          className="btn btn-sm" // Added btn-sm for smaller button
          style={{
            textDecoration: 'none',
            display: 'inline-block',
            marginTop: '1rem',
            marginBottom: '1rem'
          }}
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancel' : 'Add New'}
        </button>
         {/* Add AI Menu Button Here */}
         <button 
           type="button"
           onClick={() => navigate(`/ai-menu/${restaurantId}`)} 
           className="btn btn-sm btn-outline ai-menu-button" // Added btn-sm
           style={{
             textDecoration: 'none',
             display: 'inline-block',
             marginTop: '1rem',
             marginBottom: '1rem',
           }}
         >
           AI Menu
         </button>
     
      </div>

       {showQrCode && qrCodeUrl && (
         <div className="qr-code-section mt-4 text-center">
           <h3>Scan to View Menu</h3>
           <div style={{ margin: '1rem auto', display: 'inline-block' }}>
             <QRCodeCanvas id="qr-code-canvas" value={qrCodeUrl} size={200} />
           </div>
           <br />
           <button
             type="button"
             className="btn mt-2"
             onClick={downloadQrCode}
           >
             Download 
           </button>
           <button
             type="button"
             className="btn btn-outline mt-2 ml-2"
             onClick={() => setShowQrCode(false)}
           >
             Close
           </button>
         </div>
       )}

       {showForm && (
        <div className="item-form mt-4">
          {/* Header row: align title and portion-wise toggle; swap order when adding */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
            {editingItemId ? (
              <>
                <h3 style={{ margin: 0 }}>Edit Menu Item</h3>
                <label htmlFor="isPortionWise" style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
                  <Switch
                    isOn={formData.isPortionWise}
                    handleToggle={(e) => {
                      const newValue = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        isPortionWise: newValue
                      }));
                    }}
                  />
                  <span style={{ marginLeft: '10px' }}>Portion Wise Pricing</span>
                </label>
              </>
            ) : (
              <>
                <label htmlFor="isPortionWise" style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
                  <Switch
                    isOn={formData.isPortionWise}
                    handleToggle={(e) => {
                      const newValue = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        isPortionWise: newValue
                      }));
                    }}
                  />
                  <span style={{ marginLeft: '10px' }}>Portion Wise Pricing</span>
                </label>
                <h3 style={{ margin: 0 }}>Add New Menu Item</h3>
              </>
            )}
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Item Name*</label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Category*</label>
              <input
                type="text"
                id="category"
                name="category"
                className="form-control"
                value={formData.category}
                onChange={handleInputChange}
                required
                placeholder="e.g. Starters, Mains, Dessert"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            
            {/* Portion-wise toggle moved to header for alignment with title */}
            
            {!formData.isPortionWise ? (
              <>
                <div className="form-group">
                  <label htmlFor="price">Price* ({getCurrencySymbol(restaurant?.currency)})</label>
                  <input
                    type="text"
                    id="price"
                    name="price"
                    className="form-control"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. 12.99"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="offerPrice">Offer Price ({getCurrencySymbol(restaurant?.currency)})</label>
                  <input
                    type="text"
                    id="offerPrice"
                    name="offerPrice"
                    className="form-control"
                    value={formData.offerPrice}
                    onChange={handleInputChange}
                    placeholder="e.g. 9.99"
                  />
                </div>
              </>
            ) : (
              <div className="portion-management">
                <div className="portion-header">
                  <h4>Portions</h4>
                  <button
                    type="button"
                    onClick={handleAddPortion}
                    className="add-portion-btn"
                  >
                    <FaPlus /> Add Portion
                  </button>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '6px' }}>
                  Minimum 2 portions required when portion-wise pricing is enabled.
                </div>
                
                {formData.portions.length > 0 ? (
                  <div className="portions-list">
                    {formData.portions.map((portion) => (
                      <div key={portion.id} className="portion-item">
                        <div className="portion-info">
                          <span className="portion-name">{portion.name}</span>
                          <span className="portion-price">{getCurrencySymbol(restaurant?.currency)}{portion.price}</span>
                          {portion.description && (
                            <span className="portion-description">{portion.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeletePortion(portion.id)}
                          className="delete-portion-btn"
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="portions-empty">
                    <FaUtensils />
                    <p>No portions added yet. Click "Add Portion" to create your first portion.</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="prepTime">Preparation Time (minutes)</label>
              <input
                type="text"
                id="prepTime"
                name="prepTime"
                className="form-control"
                value={formData.prepTime}
                onChange={handleInputChange}
                placeholder="e.g. 15"
              />
            </div>
            
            <div className="form-group">
              <label>Item Photo</label>
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="preview-img" />
              ) : (
                <div className="upload-btn" onClick={() => document.getElementById('photoInput').click()}>
                  <span>+ Upload Photo</span>
                </div>
              )}
              <input
                type="file"
                id="photoInput"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
              {photoPreview && (
                <button 
                  type="button" 
                  className="btn btn-outline mt-2"
                  onClick={() => {
                    setPhotoFile(null);
                    setPhotoPreview(null);
                    setFormData({
                      ...formData,
                      photoUrl: ''
                    });
                  }}
                >
                  Remove Photo
                </button>
              )}
            </div>
            
            <div className="form-group">
              <button type="submit" className="btn" disabled={isSubmitting}>
                {isSubmitting ? 
                  (uploadProgress > 0 && uploadProgress < 100 ? 
                    `Uploading... ${uploadProgress.toFixed(0)}%` : 
                    'Saving...') : 
                  (editingItemId ? 'Update Item' : 'Add Item')}
              </button>
              
              {/* Show progress bar when uploading */}
              {isSubmitting && uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress-container" style={{ marginTop: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div 
                    className="upload-progress-bar" 
                    style={{ 
                      width: `${uploadProgress}%`, 
                      backgroundColor: '#2E8B57', 
                      height: '8px',
                      transition: 'width 0.3s ease'
                    }}
                  ></div>
                </div>
              )}
              
              {editingItemId && (
                <button 
                  type="button" 
                  className="btn delete-btn"
                  style={{ marginTop: '10px',}}
                  onClick={() => handleDeleteItem(editingItemId)}
                  disabled={isSubmitting}
                >
                  Delete Item
                </button>
              )}
            </div>
          </form>
        </div>
      )}
      
      <div className="menu-list">
        <div className="search-container" style={{
          marginBottom: '20px',
          width: '100%',
          position: 'relative'
        }}>
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
            style={{
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              width: '100%',
              fontSize: '1rem'
            }}
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '25px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.2rem',
                color: '#888'
              }}
            >
              ×
            </button>
          )}
        </div>
        <h3 className="mb-4">Current Menu Items</h3>
        
        {Object.keys(groupedItems).length === 0 ? (
          <p>No menu items yet. Add your first item above.</p>
        ) : (
          Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} className="category-section mb-4">
              <h4 className="category-title">{category}</h4>
              
              <div className="menu-grid">
                {items.map(item => (
                  <div key={item.id} className={`menu-item ${item.isHidden ? 'item-hidden' : ''}`}>
                    
                    {item.photoUrl ? (
                      <img src={item.photoUrl} alt={item.name} className="item-photo" />
                    ) : (
                      <div className="menu-item-placeholder"></div>
                    )}
                    
                    {item.offerPrice && item.offerPrice < item.price && (
                      <div className="offer-badge">Offer</div>
                    )}
                    
               <div className="menu-item-content">
                <div className="menu-item-category" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                    width: '100%'
                }}>
                    <h3 className="menu-item-title" style={{
                    margin: '0' // Remove default margin to align properly
                    }}>{item.name}</h3>
                    
                    {/* Quick toggle switch */}
                    <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    flexShrink: 0 // Prevent the switch from shrinking
                    }}>
                    <Switch
                        isOn={!item.isHidden}
                        handleToggle={() => handleToggleVisibility(item.id, item.isHidden)}
                    />
                    <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap' // Prevent text wrapping
                    }}>
                        {item.isHidden ? 'Hidden' : 'Visible'}
                    </span>
                    </div>
                </div>
                      {item.description && (
                        <p className="menu-item-description">{item.description}</p>
                      )}
                      
                      {/* Portion Selection - Only for portion-wise items (dynamic) */}
                      {item.isPortionWise && item.portions && item.portions.length > 0 && (
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            gap: '15px', 
                            marginBottom: '10px', 
                            justifyContent: 'left', 
                            flexWrap: 'wrap' 
                          }}>
                            {item.portions.map(portion => (
                              <label 
                                key={portion.id || portion.name} 
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  cursor: 'pointer', 
                                  fontSize: '0.9rem', 
                                  fontWeight: selectedPortions[item.id] === portion.name ? 'bold' : 'normal', 
                                  color: selectedPortions[item.id] === portion.name ? '#2563eb' : '#666' 
                                }}
                              >
                                <input 
                                  type="radio" 
                                  name={`portion-${item.id}`} 
                                  value={portion.name} 
                                  checked={selectedPortions[item.id] === portion.name} 
                                  onChange={() => handlePortionChange(item.id, portion.name)} 
                                  style={{ marginRight: '5px' }} 
                                />
                                {portion.name}
                              </label>
                            ))}
                          </div>
                          {(() => {
                            const selectedName = selectedPortions[item.id] || item.portions[0].name;
                            const selected = item.portions.find(p => p.name === selectedName);
                            return selected && selected.description ? (
                              <div style={{ 
                                fontSize: '0.85rem', 
                                color: '#666', 
                                marginBottom: '10px',
                                lineHeight: '1.3',
                                fontStyle: 'italic',
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #e9ecef' 
                              }}>
                                {selected.description}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}
                      
                      <div className="menu-item-footer" style={{marginTop:10}}>
                        <div className="menu-item-price" >
                          {/* Use dynamic currency symbol and display price based on portion selection */}
                          {item.isPortionWise ? (
                            <span style={{ fontWeight: 'bold' }}>
                              {getCurrencySymbol(restaurant?.currency)}{" "}{getDisplayPrice(item)?.toFixed(2)}
                            </span>
                          ) : item.offerPrice && item.offerPrice < item.price ? (
                            <>
                                {/* Offer price - displayed prominently */}
                                <span style={{ 
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                color: '#2563eb'
                              }}>
                                {getCurrencySymbol(restaurant?.currency)}{" "}{item.offerPrice.toFixed(2)}
                              </span>
                              {/* Original price - displayed smaller with strikethrough */}
                              <span style={{ 
                                textDecoration: 'line-through', 
                                color: '#888', 
                                marginLeft: '8px',
                                fontSize: '0.8rem'
                              }}>
                                {getCurrencySymbol(restaurant?.currency)}{" "}{item.price}
                              </span>
                          
                            </>
                          ) : (
                            <span style={{ fontWeight: 'bold' }}>
                              {getCurrencySymbol(restaurant?.currency)}{" "}{item.price}
                            </span>
                          )}
                        </div>
                        {item.prepTime && (
                          <div className="menu-item-prep" style={{marginTop:10}}>
                            <i className="far fa-clock"></i>
                            {item.prepTime} min
                          </div>
                        )}
                      </div>
                      
                      <div className="item-actions" style={{
                        display: 'flex',
                        flexWrap: 'wrap',  // Allow buttons to wrap on small screens
                        gap: '8px',       // Add consistent spacing between buttons
                        marginTop: '10px'
                        }}>
                        <button 
                          className="btn btn-outline"
                          onClick={() => handleEditItem(item)}
                          style={{
                            padding: '6px 12px',   // Reduce padding slightly
                            fontSize: '0.9rem',    // Smaller font size
                            flex: '1',             // Allows buttons to grow but not beyond content
                            minWidth: '70px',      // Minimum width
                            maxWidth: '100px'      // Maximum width
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn delete-btn"
                          onClick={() => handleDeleteItem(item.id)}
                          style={{
                            padding: '6px 12px',   // Reduce padding slightly
                            fontSize: '0.9rem',    // Smaller font size
                            flex: '1',             // Allows buttons to grow but not beyond content
                            minWidth: '70px',      // Minimum width
                            maxWidth: '100px',     // Maximum width
                            color: 'black'         // Set text color to black
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default EditMenuPage;